import { FC } from 'react'
import { Button, ButtonGroup } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faArrowLeft,
    faDesktop,
    faTabletScreenButton,
    faMobileScreenButton,
    faFloppyDisk,
    faCircleNotch,
    faTableColumns,
    faPlus,
    faRotateLeft,
} from '@fortawesome/free-solid-svg-icons'
import { useAppSelector } from '../../../../../store/hooks'
import { Language } from '../../../../../features/security/security.slice'
import { EditorToolbarProps, DevicePreview } from '../types'

const devices: { key: DevicePreview; icon: typeof faDesktop; label: string }[] = [
    { key: 'desktop', icon: faDesktop, label: 'Desktop' },
    { key: 'tablet', icon: faTabletScreenButton, label: 'Tablet' },
    { key: 'mobile', icon: faMobileScreenButton, label: 'Mobile' },
]

const EditorToolbar: FC<EditorToolbarProps> = ({
    pageName,
    isSaving,
    isDirty,
    devicePreview,
    onDeviceChange,
    onSave,
    onBack,
    rightPanelOpen,
    onToggleRightPanel,
    activeLocale,
    onLocaleChange,
    onAddSection,
    onReset,
}) => {
    const theme = useAppSelector((state) => state.theme.theme)
    const initData = useAppSelector((state: any) => state.security.initData)

    return (
        <div
            className={`sfy-editor-toolbar ${
                theme === 'dark' ? 'sfy-editor-toolbar--dark' : ''
            }`}
        >
            <div className="sfy-editor-toolbar__left">
                <button
                    type="button"
                    className="sfy-editor-toolbar__back-btn"
                    onClick={onBack}
                    title="Back to pages"
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                </button>
                <div className="sfy-editor-toolbar__page-info">
                    <span className="sfy-editor-toolbar__page-name">
                        {pageName}
                    </span>
                    {isDirty && (
                        <span className="sfy-editor-toolbar__unsaved-badge">
                            Unsaved
                        </span>
                    )}
                </div>
            </div>

            <div className="sfy-editor-toolbar__center">
                <ButtonGroup size="sm" className="sfy-device-toggle">
                    {devices.map((device) => (
                        <Button
                            key={device.key}
                            variant={
                                devicePreview === device.key
                                    ? 'primary'
                                    : theme === 'dark'
                                    ? 'outline-light'
                                    : 'outline-secondary'
                            }
                            onClick={() => onDeviceChange(device.key)}
                            title={device.label}
                            className="sfy-device-toggle__btn"
                        >
                            <FontAwesomeIcon icon={device.icon} />
                        </Button>
                    ))}
                </ButtonGroup>

                <ButtonGroup size="sm" className="sfy-locale-toggle ms-3">
                    {Object.keys(initData.languages.data).map((locale) => {
                        const lang: Language = initData.languages.data[locale]
                        return (
                            <Button
                                key={locale}
                                variant={
                                    activeLocale === locale
                                        ? 'primary'
                                        : theme === 'dark'
                                        ? 'outline-light'
                                        : 'outline-secondary'
                                }
                                onClick={() => onLocaleChange(locale)}
                                title={lang.name}
                                className="sfy-locale-toggle__btn"
                            >
                                {locale.toUpperCase()}
                            </Button>
                        )
                    })}
                </ButtonGroup>
            </div>

            <div className="sfy-editor-toolbar__right">
                <Button
                    variant="outline-primary"
                    size="sm"
                    className="sfy-editor-toolbar__add-section-btn"
                    onClick={onAddSection}
                >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    <span className="d-none d-md-inline">Add Section</span>
                </Button>
                <button
                    type="button"
                    className={`sfy-editor-toolbar__panel-toggle ${
                        rightPanelOpen
                            ? 'sfy-editor-toolbar__panel-toggle--active'
                            : ''
                    }`}
                    onClick={onToggleRightPanel}
                    title={rightPanelOpen ? 'Hide properties panel' : 'Show properties panel'}
                >
                    <FontAwesomeIcon icon={faTableColumns} />
                </button>
                <Button
                    variant={theme === 'dark' ? 'outline-light' : 'outline-secondary'}
                    size="sm"
                    className="sfy-editor-toolbar__reset-btn"
                    onClick={onReset}
                    disabled={isSaving || !isDirty}
                    title="Reset all changes"
                >
                    <FontAwesomeIcon icon={faRotateLeft} />
                    <span className="ms-2 d-none d-lg-inline">Reset</span>
                </Button>
                <Button
                    variant="success"
                    size="sm"
                    className="sfy-editor-toolbar__save-btn"
                    onClick={onSave}
                    disabled={isSaving}
                >
                    {isSaving ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin />
                            <span className="ms-2">Saving...</span>
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faFloppyDisk} />
                            <span className="ms-2">Save</span>
                        </>
                    )}
                </Button>
            </div>
        </div>
    )
}

export default EditorToolbar
